import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environments';

/** Usuarios admin de pedidos MOBICO: en vez de la vista de solicitante ven el
 *  panel admin (segmentación 2026-07-13: se sumaron precios, expansión y
 *  gerencia comercial al aux de pedidos original). */
export const MOBICO_PIPES_ADMIN_EMAILS = [
  'aux.pedidos@mobico.com.mx',
  'expansion@mobico.com.mx',
  'g.comercial@mobico.com.mx',
];

/** Grupo SOLICITANTE del flujo de pipas: MOBICO aún no confirma con qué grupo
 *  se va a probar (2026-07-13). '' = la vista de solicitante no le aparece a
 *  NADIE (menú y páginas de estación ocultos); cuando confirmen, poner aquí el
 *  _id del grupo. No afecta al panel admin (gateado por email, abajo). */
export const MOBICO_PIPES_REQUESTER_GROUP = '';

/** true si el grupo de la sesión es el grupo solicitante del flujo de pipas. */
export function isMobicoPipesRequester(): boolean {
  if (!MOBICO_PIPES_REQUESTER_GROUP) { return false; }
  try {
    return (localStorage.getItem('group') || '') === MOBICO_PIPES_REQUESTER_GROUP;
  } catch {
    return false;
  }
}

/** true si la sesión actual es un usuario admin de pedidos MOBICO. */
export function isMobicoPipesAdmin(): boolean {
  try {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const email = currentUser?.user?.email || currentUser?.user?.username || '';
    return MOBICO_PIPES_ADMIN_EMAILS.includes(String(email).toLowerCase());
  } catch {
    return false;
  }
}

/**
 * Servicio de Pedidos MOBICO (Fuel Orders).
 * Backend: /v1/gasprecio/fuel-orders (branch feature/mobico_inventario).
 * El BasicAuthInterceptor agrega el header Authorization automáticamente.
 */
@Injectable({ providedIn: 'root' })
export class FuelOrdersService {
  private endpoint = environment.apiUrl + '/v1/gasprecio/fuel-orders';
  private invEndpoint = environment.apiUrl + '/v1/gasprecio/inventory_managment';

  constructor(private http: HttpClient) {}

  /** Cronograma semanal (timeline): stock proyectado + volume_pending (pedidos) + volume_purchase (confirmados). */
  getTimeline(params: { group?: string; zones?: string; start_date?: string; end_date?: string }): Observable<any> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') { httpParams = httpParams.set(k, String(v)); }
    });
    return this.http.get(`${this.invEndpoint}/timeline`, { params: httpParams }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /** Lista de pedidos de la semana por estación (estación ve solo lo suyo). */
  getWeekOrders(
    params: { group?: string; zones?: string; week?: number | string; product?: string } = {}
  ): Observable<any> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        httpParams = httpParams.set(k, String(v));
      }
    });
    return this.http.get(`${this.endpoint}/week`, { params: httpParams }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /** Crea/reemplaza los pedidos de una estación+producto+semana. */
  saveOrders(body: any): Observable<any> {
    return this.http.post(`${this.endpoint}`, body).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /** Edita una solicitud (pipa) existente. */
  updateFullOrder(id: string, body: any): Observable<any> {
    return this.http.put(`${this.endpoint}/${id}`, body).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /**
   * MOBICO (admin) responde una solicitud: confirmed / rejected / review.
   * Al aprobar un pedido primario, delivery_date es la fecha de entrega
   * confirmada que captura el admin (YYYY-MM-DD).
   */
  updateOrderStatus(id: string, body: { status?: string; mobico_response?: string; delivery_date?: string; liters?: number; total?: number; supply_price?: number }): Observable<any> {
    return this.http.patch(`${this.endpoint}/${id}`, body).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /**
   * Registra una solicitud de cambio sobre un pedido existente (doc hijo con
   * change_type: date | capacity | product | cancel y el valor nuevo).
   */
  requestChange(id: string, body: any): Observable<any> {
    return this.http.post(`${this.endpoint}/${id}/change`, body).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /** Solicitudes de cambio de la semana agrupadas por tipo (addition/cancel/date/capacity/product). */
  getChanges(
    params: { group?: string; zones?: string; week?: string; product?: string; station?: string } = {}
  ): Observable<any> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') { httpParams = httpParams.set(k, String(v)); }
    });
    return this.http.get(`${this.endpoint}/changes`, { params: httpParams }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /** Borra una solicitud (pipa). */
  deleteOrder(id: string): Observable<any> {
    return this.http.delete(`${this.endpoint}/${id}`).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }

  /**
   * Sugerencia automática de litros/fecha para pre-llenar el formulario.
   * Con `date`, available_space viene proyectado a esa fecha de entrega (candado de
   * inventario); `exclude` ignora una pipa en el cálculo (la que se está editando).
   */
  getSuggest(station: string, product: string, date?: string, exclude?: string): Observable<any> {
    let params = new HttpParams().set('station', station).set('product', product);
    if (date) { params = params.set('date', date); }
    if (exclude) { params = params.set('exclude', exclude); }
    return this.http.get(`${this.endpoint}/suggest`, { params }).pipe(
      map((r: any) => r?.data),
      catchError((e) => throwError(e))
    );
  }
}
